import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatSEK, formatMonthYear } from '../../lib/format-utils';
import { getAdminExpenseReports, getReceiptFileBlob, reviewExpenseReport, getAdminExpenseReportDetails, getExpenseReopenRequests, reviewExpenseReopenRequest, deleteExpenseReport, reopenExpenseReport } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../lib/errors';
import { Receipt, Check, X, History, DollarSign, Eye, Car, Utensils, FileText, MapPin, Calendar, Paperclip, RefreshCw, Trash2, AlertCircle } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import SupplierInvoicePreview from '../../components/supplier-invoice-preview';

export default function ExpenseApprovalsPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [historyReports, setHistoryReports] = useState([]);
    const [reopenRequests, setReopenRequests] = useState([]);
    const [activeTab, setActiveTab] = useState('pending');
    const [processing, setProcessing] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [viewReport, setViewReport] = useState(null);
    const [reportExpenses, setReportExpenses] = useState([]);
    const [loadingExpenses, setLoadingExpenses] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [reportToDelete, setReportToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    // Admin-initiated send-back (14.6 / KD-076) — distinct from the reject
    // dialog above: targets an already-reviewed (approved/rejected) report
    // via the new reopen endpoint, reachable from the history table and
    // from the details dialog footer.
    const [sendBackDialogOpen, setSendBackDialogOpen] = useState(false);
    const [sendBackComment, setSendBackComment] = useState('');
    const [sendBackTarget, setSendBackTarget] = useState(null);
    // Legacy base64 receipts (receipt_data) open in the viewer dialog;
    // disk-stored receipts (receipt_path) open in the side preview pane of
    // the details dialog so they can be checked before approving.
    const [viewingReceipt, setViewingReceipt] = useState(null);
    const [previewReceipt, setPreviewReceipt] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            // History = approved + rejected, mirroring ApprovalsPage's
            // getTimesheetHistory (timesheets.py returns status $in
            // ["approved", "rejected"] from a single endpoint; the expense
            // reports endpoint only takes one status at a time, so both are
            // fetched here and merged). This also keeps a report the admin
            // just sent back (14.6 / KD-076 — status becomes "rejected")
            // visible in history instead of disappearing entirely.
            // Only the PENDING queue is load-bearing: it is the work the admin
            // came here to do. History and reopen requests are context, so each
            // falls back to empty rather than taking the whole page down with
            // it — one failing history call used to blank the approval queue
            // itself. Same shape as SupplierInvoicesPage::fetchAll.
            const [pendingRes, approvedRes, rejectedRes, reopenRes] = await Promise.all([
                getAdminExpenseReports('submitted'),
                getAdminExpenseReports('approved').catch(() => ({ data: [] })),
                getAdminExpenseReports('rejected').catch(() => ({ data: [] })),
                getExpenseReopenRequests().catch(() => ({ data: [] })),
            ]);
            const history = [...approvedRes.data, ...rejectedRes.data].sort(
                (a, b) => new Date(b.reviewed_at || 0) - new Date(a.reviewed_at || 0)
            );
            setReports(pendingRes.data);
            setHistoryReports(history);
            setReopenRequests(reopenRes.data);
        } catch (error) {
            toast.error(t('expenses.failed_load'));
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (report) => {
        setProcessing(true);
        try {
            await reviewExpenseReport(report.user_id, report.year, report.month, 'approve');
            toast.success(t('expenses.report_approved'));
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('expenses.failed_approve'));
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedReport) return;
        setProcessing(true);
        try {
            await reviewExpenseReport(selectedReport.user_id, selectedReport.year, selectedReport.month, 'reject', rejectComment);
            toast.success(t('expenses.report_rejected'));
            setRejectDialogOpen(false);
            setRejectComment('');
            setSelectedReport(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('expenses.failed_reject'));
        } finally {
            setProcessing(false);
        }
    };

    const handleReopenAction = async (requestId, action) => {
        setProcessing(true);
        try {
            await reviewExpenseReopenRequest(requestId, action);
            toast.success(
                action === 'approve'
                    ? t('expenses.reopen_approved')
                    : t('expenses.reopen_rejected')
            );
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('expenses.failed_load'));
        } finally {
            setProcessing(false);
        }
    };

    const openDeleteDialog = (report) => {
        setReportToDelete(report);
        setDeleteDialogOpen(true);
    };

    const handleDeleteReport = async () => {
        if (!reportToDelete) return;
        setDeleting(true);
        try {
            await deleteExpenseReport(reportToDelete.user_id, reportToDelete.year, reportToDelete.month);
            toast.success(t('expenses.delete_report_success'));
            setDeleteDialogOpen(false);
            setReportToDelete(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || t('expenses.delete_report_failed'));
        } finally {
            setDeleting(false);
        }
    };

    const openRejectDialog = (report) => {
        setSelectedReport(report);
        setRejectDialogOpen(true);
    };

    const openSendBackDialog = (report) => {
        setSendBackTarget(report);
        setSendBackComment('');
        setSendBackDialogOpen(true);
    };

    const handleSendBack = async () => {
        if (!sendBackTarget || !sendBackComment.trim()) return;
        setProcessing(true);
        try {
            await reopenExpenseReport(sendBackTarget.user_id, sendBackTarget.year, sendBackTarget.month, sendBackComment);
            toast.success(t('expenses.send_back_success'));
            setSendBackDialogOpen(false);
            setSendBackTarget(null);
            setSendBackComment('');
            setViewReport(null);
            fetchData();
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('expenses.send_back_failed')));
        } finally {
            setProcessing(false);
        }
    };

    const viewReportDetails = async (report) => {
        setViewReport(report);
        setPreviewReceipt(null);
        setLoadingExpenses(true);
        try {
            const res = await getAdminExpenseReportDetails(report.user_id, report.year, report.month);
            setReportExpenses(res.data);
        } catch (error) {
            toast.error(t('expenses.failed_details'));
            setReportExpenses([]);
        } finally {
            setLoadingExpenses(false);
        }
    };

    // Friendly name helpers
    const getReceiptTypeName = (typeId) => {
        const typeNames = {
            'travel_flight': 'Flight Ticket',
            'travel_train': 'Train Ticket',
            'travel_taxi': 'Taxi / Uber',
            'travel_parking': 'Parking',
            'travel_fuel': 'Fuel / Gas',
            'travel_other': 'Other Travel',
            'accommodation': 'Hotel / Accommodation',
            'food_meals': 'Meals & Food',
            'office_supplies': 'Office Supplies',
            'equipment': 'Equipment',
            'software': 'Software License',
            'phone': 'Phone / Communication',
            'other': 'Other Expense'
        };
        return typeNames[typeId] || typeId;
    };

    const getAllowanceTypeName = (typeId) => {
        const typeNames = {
            'domestic_day': 'Domestic Day Trip',
            'domestic_overnight': 'Domestic Overnight',
            'international_day': 'International Day Trip',
            'international_overnight': 'International Overnight'
        };
        return typeNames[typeId] || typeId;
    };

    const getVehicleTypeName = (typeId) => {
        const typeNames = {
            'own_car': 'Own Car',
            'company_car': 'Company Car',
            'electric_car': 'Electric Car',
            'motorcycle': 'Motorcycle'
        };
        return typeNames[typeId] || typeId;
    };

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
    ];

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'mileage': return <Car className="w-4 h-4" />;
            case 'receipt': return <Receipt className="w-4 h-4" />;
            case 'allowance': return <Utensils className="w-4 h-4" />;
            default: return <FileText className="w-4 h-4" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-foreground border-t-transparent animate-spin"></div>
            </div>
        );
    }


    return (
        <div className="space-y-6" data-testid="expense-approvals-page">
            {/* Header */}
            <div>
                <h1 className="font-heading text-4xl font-bold tracking-tight">{t('expenses.title')}</h1>
                <p className="text-muted-foreground mt-1">{t('expenses.subtitle')}</p>
            </div>

            {/* Pending Reopen Requests — shown above stats when present */}
            {reopenRequests.length > 0 && (
                <div>
                    <h2 className="font-heading text-xl font-bold flex items-center gap-2 mb-4">
                        <RefreshCw className="w-5 h-5" />
                        {t('expenses.reopen_requests_title', { count: reopenRequests.length })}
                    </h2>
                    <div className="space-y-3">
                        {reopenRequests.map((request) => (
                            <Card key={request.id} className="border border-amber-300 bg-amber-50 shadow-none">
                                <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="font-medium">{request.user_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {t('expenses.reopen_request_period', {
                                                    month: monthNames[request.month - 1] || request.month,
                                                    year: request.year,
                                                })}
                                            </p>
                                            <p className="text-sm mt-2 italic">
                                                &ldquo;{request.reason}&rdquo;
                                            </p>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t('approvals.requested', {
                                                    date: new Date(request.requested_at).toLocaleString(),
                                                })}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleReopenAction(request.id, 'reject')}
                                                disabled={processing}
                                                className="text-destructive border-destructive"
                                            >
                                                <X className="w-4 h-4" />
                                                {t('expenses.reopen_reject')}
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleReopenAction(request.id, 'approve')}
                                                disabled={processing}
                                            >
                                                <Check className="w-4 h-4 mr-1" />
                                                {t('expenses.reopen_approve')}
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('expenses.pending_reports')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{reports.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                            </div>
                            <div className="min-w-0 overflow-hidden">
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('expenses.total_pending_amount')}</p>
                                <p className="text-base sm:text-2xl font-bold font-mono break-all">
                                    {formatSEK(reports.reduce((sum, r) => sum + (r.grand_total || 0), 0))}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="border border-border shadow-none">
                    <CardContent className="p-3 sm:pt-6">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-3">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 flex items-center justify-center flex-shrink-0 mb-1 sm:mb-0">
                                <Check className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                                {/* History now includes rejected/sent-back reports alongside
                                    approved ones (parity with ApprovalsPage) — "Reviewed" is
                                    accurate for both, "Approved This Month" no longer is. */}
                                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase leading-tight">{t('approvals.stat_reviewed')}</p>
                                <p className="text-lg sm:text-2xl font-bold font-mono">{historyReports.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pending / History Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="pending">
                        {t('approvals.tab_pending')} ({reports.length})
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        {t('approvals.tab_history')} ({historyReports.length})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pending" className="mt-4">
                    <Card className="border border-border shadow-none">
                        <CardContent className="p-6">
                            {reports.length === 0 ? (
                                <div className="text-center py-12 text-muted-foreground">
                                    <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                    <p>{t('expenses.no_pending')}</p>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {reports.map(report => (
                                        <div
                                            key={`${report.user_id}-${report.year}-${report.month}`}
                                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors gap-3"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary font-bold flex-shrink-0">
                                                    {report.user_name?.charAt(0) || '?'}
                                                </div>
                                                <div>
                                                    <p className="font-medium">{report.user_name}</p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {formatMonthYear(report.year, report.month)} • {t('expenses.items', { count: report.items_count })}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3">
                                                <div className="min-w-0 overflow-hidden">
                                                    <div className="flex gap-3 text-xs mb-1">
                                                        <span className="text-blue-600 flex items-center gap-1"><Car className="w-3 h-3" /> {report.mileage_total?.toFixed(0) || 0} kr</span>
                                                        <span className="text-green-600 flex items-center gap-1"><Receipt className="w-3 h-3" /> {report.receipts_total?.toFixed(0) || 0} kr</span>
                                                        <span className="text-amber-600 flex items-center gap-1"><Utensils className="w-3 h-3" /> {report.allowances_total?.toFixed(0) || 0} kr</span>
                                                    </div>
                                                    <p className="text-lg sm:text-xl font-bold font-mono break-all">{formatSEK(report.grand_total || 0)}</p>
                                                    {report.reimbursable_total > 0 && (
                                                        <p className="text-xs text-green-600">
                                                            {t('expenses.reimbursable', { amount: report.reimbursable_total?.toFixed(0) })}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => viewReportDetails(report)}
                                                        title={t('expenses.view_details')}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-green-600 hover:text-green-700"
                                                        onClick={() => handleApprove(report)}
                                                        disabled={processing}
                                                    >
                                                        <Check className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        className="text-red-600 hover:text-red-700"
                                                        onClick={() => openRejectDialog(report)}
                                                        disabled={processing}
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history" className="mt-4">
                    <Card className="border border-border shadow-none">
                        <CardContent className="p-0">
                            {historyReports.length === 0 ? (
                                <div className="p-8 text-center text-muted-foreground">
                                    <p>{t('expenses.no_history')}</p>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>{t('approvals.history_employee')}</th>
                                                <th>{t('approvals.history_period')}</th>
                                                <th>{t('expenses.history_total')}</th>
                                                <th>{t('approvals.history_status')}</th>
                                                <th>{t('approvals.history_reviewed')}</th>
                                                <th>{t('approvals.history_actions')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyReports.map((report) => (
                                                <tr key={`${report.user_id}-${report.year}-${report.month}`} className="opacity-75">
                                                    <td className="font-medium">{report.user_name}</td>
                                                    <td>{formatMonthYear(report.year, report.month)}</td>
                                                    <td>
                                                        {/* Per-category totals + reimbursable figure preserved
                                                            from the old card list (14.6 — do not drop info). */}
                                                        <div className="flex gap-2 text-[10px] text-muted-foreground mb-1">
                                                            <span className="text-blue-600 flex items-center gap-0.5"><Car className="w-3 h-3" /> {report.mileage_total?.toFixed(0) || 0} kr</span>
                                                            <span className="text-green-600 flex items-center gap-0.5"><Receipt className="w-3 h-3" /> {report.receipts_total?.toFixed(0) || 0} kr</span>
                                                            <span className="text-amber-600 flex items-center gap-0.5"><Utensils className="w-3 h-3" /> {report.allowances_total?.toFixed(0) || 0} kr</span>
                                                        </div>
                                                        <div className="font-mono font-bold">{formatSEK(report.grand_total || 0)}</div>
                                                        {report.reimbursable_total > 0 && (
                                                            <div className="text-[10px] text-green-600">
                                                                {t('expenses.reimbursable', { amount: report.reimbursable_total?.toFixed(0) })}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`px-2 py-1 text-xs uppercase tracking-wider ${
                                                            report.status === 'approved'
                                                                ? 'bg-green-600 text-white'
                                                                : 'bg-destructive text-white'
                                                        }`}>
                                                            {t(`common.${report.status}`, { defaultValue: report.status })}
                                                        </span>
                                                    </td>
                                                    <td className="text-sm text-muted-foreground">
                                                        {report.reviewed_at ? (
                                                            <div>
                                                                <span>{new Date(report.reviewed_at).toLocaleDateString()}</span>
                                                                {report.reviewer_name && (
                                                                    <span className="block text-xs">{t('approvals.reviewed_by', { name: report.reviewer_name })}</span>
                                                                )}
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => viewReportDetails(report)}
                                                                title={t('common.view')}
                                                                aria-label={t('common.view')}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            {/* Only an APPROVED report can be sent back: the backend
                                                                accepts submitted/approved only, and a report already
                                                                "rejected" is by definition back with the employee —
                                                                offering the action there is a guaranteed 400. */}
                                                            {report.status === 'approved' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => openSendBackDialog(report)}
                                                                    title={t('expenses.send_back_action')}
                                                                    aria-label={t('expenses.send_back_action')}
                                                                    data-testid={`send-back-expense-report-${report.user_id}-${report.year}-${report.month}`}
                                                                >
                                                                    <RefreshCw className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="text-destructive hover:text-destructive"
                                                                onClick={() => openDeleteDialog(report)}
                                                                title={t('expenses.delete_report')}
                                                                aria-label={t('expenses.delete_report')}
                                                                data-testid={`delete-expense-report-${report.user_id}-${report.year}-${report.month}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Delete Expense Report Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl flex items-center gap-2 text-destructive">
                            <AlertCircle className="w-5 h-5" />
                            {t('expenses.delete_report_confirm_title')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('expenses.delete_report_confirm_body')}
                        </p>
                        {reportToDelete && (
                            <div className="p-4 bg-muted rounded-lg space-y-2">
                                <p><strong>{t('approvals.employee')}</strong> {reportToDelete.user_name}</p>
                                <p><strong>{t('approvals.period')}</strong> {formatMonthYear(reportToDelete.year, reportToDelete.month)}</p>
                                <p><strong>{t('expenses.items', { count: reportToDelete.items_count })}</strong></p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDeleteReport}
                            disabled={deleting}
                            data-testid="confirm-delete-expense-report-btn"
                        >
                            {deleting ? t('common.deleting') : t('expenses.delete_report')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reject Dialog */}
            <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('expenses.reject_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('expenses.reject_for', { name: selectedReport?.user_name, month: selectedReport ? formatMonthYear(selectedReport.year, selectedReport.month) : '', year: '' })}
                        </p>
                        <Textarea
                            placeholder={t('expenses.reject_placeholder')}
                            value={rejectComment}
                            onChange={(e) => setRejectComment(e.target.value)}
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleReject}
                            disabled={processing}
                        >
                            {processing ? t('common.rejecting') : t('expenses.reject_report')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Send Back Dialog — admin-initiated correction request on an
                already-reviewed report (14.6 / KD-076) */}
            <Dialog open={sendBackDialogOpen} onOpenChange={setSendBackDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{t('expenses.send_back_title')}</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-sm text-muted-foreground mb-4">
                            {t('expenses.send_back_for', { name: sendBackTarget?.user_name, month: sendBackTarget ? formatMonthYear(sendBackTarget.year, sendBackTarget.month) : '' })}
                        </p>
                        <Textarea
                            placeholder={t('expenses.send_back_placeholder')}
                            value={sendBackComment}
                            onChange={(e) => setSendBackComment(e.target.value)}
                            rows={3}
                            data-testid="send-back-comment-input"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSendBackDialogOpen(false)}>
                            {t('common.cancel')}
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleSendBack}
                            disabled={processing || !sendBackComment.trim()}
                            data-testid="confirm-send-back-btn"
                        >
                            {processing ? t('common.processing') : t('expenses.send_back_report')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* View Report Details Dialog */}
            <Dialog open={!!viewReport} onOpenChange={() => setViewReport(null)}>
                <DialogContent className={`${previewReceipt ? 'max-w-7xl' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl">{t('expenses.details_title')}</DialogTitle>
                    </DialogHeader>
                    {viewReport && (
                        <div className={previewReceipt ? 'flex flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] lg:gap-4' : ''}>
                        <div className="py-4 space-y-4 min-w-0">
                            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                                <div>
                                    <p className="font-medium text-lg">{viewReport.user_name}</p>
                                    <p className="text-sm text-muted-foreground">
                                        {formatMonthYear(viewReport.year, viewReport.month)}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-2xl font-bold font-mono">{formatSEK(viewReport.grand_total)}</p>
                                    <p className="text-sm text-muted-foreground">{t('expenses.items', { count: viewReport.items_count })}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-2 text-blue-600 mb-1">
                                            <Car className="w-4 h-4" />
                                            <span className="text-xs uppercase">{t('expenses.mileage_label')}</span>
                                        </div>
                                        <p className="text-xl font-bold font-mono">{formatSEK(viewReport.mileage_total || 0)}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-2 text-green-600 mb-1">
                                            <Receipt className="w-4 h-4" />
                                            <span className="text-xs uppercase">{t('expenses.receipts_section')}</span>
                                        </div>
                                        <p className="text-xl font-bold font-mono">{formatSEK(viewReport.receipts_total || 0)}</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="pt-4">
                                        <div className="flex items-center gap-2 text-amber-600 mb-1">
                                            <Utensils className="w-4 h-4" />
                                            <span className="text-xs uppercase">{t('expenses.allowances_section')}</span>
                                        </div>
                                        <p className="text-xl font-bold font-mono">{formatSEK(viewReport.allowances_total || 0)}</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {viewReport.reimbursable_total > 0 && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-sm">
                                        <span className="font-medium text-green-700">{t('expenses.reimbursable_label')}</span> {formatSEK(viewReport.reimbursable_total)}
                                    </p>
                                    <p className="text-xs text-green-600 mt-1">
                                        {t('expenses.reimbursable_hint')}
                                    </p>
                                </div>
                            )}

                            {/* Detailed Expense Breakdown */}
                            <div className="border-t pt-4">
                                <h3 className="font-heading font-bold mb-3">{t('expenses.detailed_breakdown')}</h3>

                                {loadingExpenses ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="w-6 h-6 border-2 border-foreground border-t-transparent animate-spin rounded-full"></div>
                                    </div>
                                ) : reportExpenses.length === 0 ? (
                                    <p className="text-muted-foreground text-center py-4">{t('expenses.no_details')}</p>
                                ) : (
                                    <div className="space-y-2">
                                        {/* Mileage Expenses */}
                                        {reportExpenses.filter(e => e.expense_category === 'mileage').length > 0 && (
                                            <div className="mb-4">
                                                <Label className="text-xs uppercase text-blue-600 flex items-center gap-1 mb-2">
                                                    <Car className="w-3 h-3" /> {t('expenses.mileage_label')} ({reportExpenses.filter(e => e.expense_category === 'mileage').length})
                                                </Label>
                                                <div className="space-y-1">
                                                    {reportExpenses.filter(e => e.expense_category === 'mileage').map(expense => (
                                                        <div key={expense.id} className="flex items-center justify-between p-2 bg-blue-50 rounded text-sm">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-muted-foreground w-20">{expense.date}</span>
                                                                <div>
                                                                    <span className="font-medium">{expense.from_location} → {expense.to_location}</span>
                                                                    <span className="text-xs text-muted-foreground ml-2">
                                                                        ({expense.distance_mil} mil{expense.round_trip ? t('expenses.round_trip') : ''})
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-muted-foreground">{getVehicleTypeName(expense.vehicle_type)}</span>
                                                                {expense.project_name && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">{expense.project_name}</span>}
                                                                <span className="font-mono font-bold">{formatSEK(expense.amount)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Receipt Expenses */}
                                        {reportExpenses.filter(e => e.expense_category === 'receipt').length > 0 && (
                                            <div className="mb-4">
                                                <Label className="text-xs uppercase text-green-600 flex items-center gap-1 mb-2">
                                                    <Receipt className="w-3 h-3" /> {t('expenses.receipts_section')} ({reportExpenses.filter(e => e.expense_category === 'receipt').length})
                                                </Label>
                                                <div className="space-y-1">
                                                    {reportExpenses.filter(e => e.expense_category === 'receipt').map(expense => (
                                                        <div key={expense.id} className="flex items-center justify-between p-2 bg-green-50 rounded text-sm">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-muted-foreground w-20">{expense.date}</span>
                                                                <div>
                                                                    <span className="font-medium">{getReceiptTypeName(expense.receipt_type)}</span>
                                                                    {expense.description && <span className="text-muted-foreground ml-2">- {expense.description}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                {(expense.receipt_data || expense.receipt_path) && (
                                                                    <Button
                                                                        variant={previewReceipt?.id === expense.id ? 'secondary' : 'ghost'}
                                                                        size="sm"
                                                                        className="h-6 px-2"
                                                                        aria-pressed={previewReceipt?.id === expense.id}
                                                                        onClick={() =>
                                                                            expense.receipt_path
                                                                                ? setPreviewReceipt(previewReceipt?.id === expense.id ? null : expense)
                                                                                : setViewingReceipt(expense)
                                                                        }
                                                                    >
                                                                        <Paperclip className="w-3 h-3 mr-1" />
                                                                        {t('expenses.view_receipt')}
                                                                    </Button>
                                                                )}
                                                                {expense.project_name && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">{expense.project_name}</span>}
                                                                <div className="text-right">
                                                                    <div className="font-mono font-bold">{formatSEK(expense.amount)}</div>
                                                                    {expense.currency && expense.currency !== 'SEK' && expense.original_amount != null && (
                                                                        <div className="text-[10px] text-muted-foreground font-mono" title={expense.exchange_rate ? `1 ${expense.currency} = ${expense.exchange_rate.toFixed(4)} SEK` : ''}>
                                                                            {expense.original_amount.toFixed(2)} {expense.currency}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Allowance Expenses */}
                                        {reportExpenses.filter(e => e.expense_category === 'allowance').length > 0 && (
                                            <div className="mb-4">
                                                <Label className="text-xs uppercase text-amber-600 flex items-center gap-1 mb-2">
                                                    <Utensils className="w-3 h-3" /> {t('expenses.allowances_section')} ({reportExpenses.filter(e => e.expense_category === 'allowance').length})
                                                </Label>
                                                <div className="space-y-1">
                                                    {reportExpenses.filter(e => e.expense_category === 'allowance').map(expense => (
                                                        <div key={expense.id} className="flex items-center justify-between p-2 bg-amber-50 rounded text-sm">
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-muted-foreground w-20">{expense.date}</span>
                                                                <div>
                                                                    <span className="font-medium">{expense.destination}</span>
                                                                    <span className="text-xs text-muted-foreground ml-2">
                                                                        ({getAllowanceTypeName(expense.allowance_type)})
                                                                    </span>
                                                                    {expense.nights > 0 && <span className="text-xs text-muted-foreground ml-1">• {t('expenses.nights', { count: expense.nights })}</span>}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                {expense.project_name && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">{expense.project_name}</span>}
                                                                <span className="font-mono font-bold">{formatSEK(expense.amount)}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {previewReceipt && (
                            <aside className="order-first lg:order-none pt-4 lg:sticky lg:top-0 lg:self-start flex flex-col gap-2">
                                <div className="flex justify-end">
                                    <Button variant="ghost" size="sm" onClick={() => setPreviewReceipt(null)}>
                                        <X className="w-4 h-4 mr-1" />
                                        {t('common.close')}
                                    </Button>
                                </div>
                                <SupplierInvoicePreview
                                    filePath={previewReceipt.receipt_path}
                                    fileName={previewReceipt.receipt_filename || previewReceipt.receipt_path.split('/').pop()}
                                    fetchBlob={getReceiptFileBlob}
                                    className="h-[60vh] lg:h-[70vh]"
                                />
                            </aside>
                        )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setViewReport(null)}>
                            {t('common.close')}
                        </Button>
                        {/* Buttons are keyed off the report's own status, not the tab it was
                            opened from (14.6 — the details dialog is now reachable from the
                            history table too, where activeTab === 'history' but a report could
                            still theoretically be pending). */}
                        {viewReport && viewReport.status === 'submitted' && (
                            <>
                                <Button
                                    variant="outline"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => {
                                        setViewReport(null);
                                        openRejectDialog(viewReport);
                                    }}
                                >
                                    <X className="w-4 h-4 mr-2" />
                                    {t('common.reject')}
                                </Button>
                                <Button
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => {
                                        handleApprove(viewReport);
                                        setViewReport(null);
                                    }}
                                    disabled={processing}
                                >
                                    <Check className="w-4 h-4 mr-2" />
                                    {t('common.approve')}
                                </Button>
                            </>
                        )}
                        {viewReport && viewReport.status !== 'submitted' && (
                            <>
                                {viewReport.status === 'approved' && (
                                    <div className="px-4 py-2 bg-green-100 text-green-800 text-sm">
                                        <Check className="w-4 h-4 inline mr-1" />
                                        {t('approvals.approved_on', { date: viewReport.reviewed_at ? new Date(viewReport.reviewed_at).toLocaleDateString() : '' })}
                                    </div>
                                )}
                                {viewReport.status === 'rejected' && (
                                    <div className="px-4 py-2 bg-red-100 text-destructive text-sm">
                                        <X className="w-4 h-4 inline mr-1" />
                                        {t('approvals.rejected_on', { date: viewReport.reviewed_at ? new Date(viewReport.reviewed_at).toLocaleDateString() : '' })}
                                    </div>
                                )}
                                {/* Send-back only for an APPROVED report — the backend
                                    accepts submitted/approved only, and a "rejected"
                                    report is already back with the employee. */}
                                {viewReport.status === 'approved' && (
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setViewReport(null);
                                            openSendBackDialog(viewReport);
                                        }}
                                        data-testid="send-back-expense-details-btn"
                                    >
                                        <RefreshCw className="w-4 h-4 mr-2" />
                                        {t('expenses.send_back_action')}
                                    </Button>
                                )}
                            </>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Receipt Image Viewer Dialog — legacy base64 receipts
                (receipt_data data URI) only; receipt_path files open in the
                side preview pane of the details dialog. */}
            <Dialog open={!!viewingReceipt} onOpenChange={() => setViewingReceipt(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{t('expenses.receipt_image_title')}</DialogTitle>
                    </DialogHeader>
                    {viewingReceipt && viewingReceipt.receipt_data && (
                        <div className="py-4">
                            {viewingReceipt.receipt_data.startsWith('data:image') ? (
                                <img
                                    src={viewingReceipt.receipt_data}
                                    alt={t('expenses.receipt_alt')}
                                    className="max-w-full max-h-[60vh] object-contain mx-auto rounded border"
                                />
                            ) : viewingReceipt.receipt_data.startsWith('data:application/pdf') ? (
                                <div className="text-center py-8">
                                    <FileText className="w-16 h-16 mx-auto mb-4 text-red-500" />
                                    <p className="text-muted-foreground mb-4">{t('expenses.pdf_receipt')}</p>
                                    <Button onClick={() => window.open(viewingReceipt.receipt_data, '_blank')}>
                                        {t('expenses.open_pdf')}
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-center text-muted-foreground">{t('expenses.cannot_preview')}</p>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setViewingReceipt(null)}>{t('common.close')}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
